// Meta Pixel AddToCart bridge for the WC Interactivity API block.
//
// Why this exists
// ---------------
// The PDP uses `wp-block-add-to-cart-with-options` — the modern Woo block
// built on the WP Interactivity API. When a shopper adds to cart, the
// block calls `/wc/store/v1/cart/add-item` (or `/wc/store/v1/batch` with
// an embedded add-item request). It does NOT emit the legacy jQuery
// `added_to_cart` event on `document.body`.
//
// `facebook-for-woocommerce` v3.7.0 listens for that jQuery event to
// fire `fbq('track', 'AddToCart', …)`. Modern block → no event → pixel
// silently misses every add-to-cart. Meta Events Manager reports
// PageView fine but AddToCart never lands.
//
// Fix
// ---
// Globally wrap `window.fetch`. Before the request goes out we attach a
// freshly-minted UUID as the X-Sentiments-FB-Event-Id header so the
// server-side CAPI bridge in includes/analytics-capi-bridge.php can read
// the SAME id and fire a matching server-side AddToCart event. When the
// successful add-item response lands we fire fbq with that same UUID as
// `eventID`. Meta sees two events with matching IDs and dedupes them
// into one — gives us full pixel+CAPI coverage for AddToCart on iPhone,
// AdBlock, cookie-decliner traffic where the browser pixel would
// otherwise be silently dropped.
//
// Composes with buy-now.js — that module temporarily wraps fetch during
// its click flow, saving and restoring the previous fetch reference
// (which is this bridge's wrapper). So buy-now's wrap nests over this
// one, and both fire on a successful add-from-Buy-Now.

const ADD_API_PATTERN = /\/wc\/store\/v1\/(cart\/add-item|batch)/;
const EVENT_ID_HEADER = 'X-Sentiments-FB-Event-Id';

// Defensive guard so we only ever wrap once even if init runs twice.
const FLAG = '__sentimentsFbqBridge';

export function initFbqBridge() {
	if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
	if (window[FLAG]) return;
	window[FLAG] = true;

	const originalFetch = window.fetch;

	window.fetch = async function (...args) {
		// Mint and inject the event_id BEFORE the request goes out so the
		// server-side CAPI hook can read it from the request headers and
		// match its event to ours. If this isn't an add-item request, the
		// helper bails and we just pass through.
		const eventId = injectEventIdHeader(args);

		const res = await originalFetch.apply(this, args);

		// Defer everything to a try/catch — never break a real request
		// because the pixel-bridge had a bad day.
		try {
			const url = getUrl(args);
			if (!url || !ADD_API_PATTERN.test(url)) return res;
			if (!res.ok) return res;
			// fbq might not exist yet (no pixel on page / consent denied).
			// Re-check at fire-time, not init-time, since FB injects the
			// stub late on some pages.
			if (typeof window.fbq !== 'function') return res;

			// Clone before reading — we don't own the original stream and
			// must hand back an unread Response.
			const cart = await res.clone().json();
			const requested = readRequestedItem(args);

			const payload = buildAddToCartPayload(cart, requested);
			if (!payload) return res;

			// 4th arg to fbq: { eventID } — Meta's documented dedup key.
			// If we generated an event_id for this request, use it. Skipping
			// the eventID is also fine (Meta still records the pixel event,
			// just can't dedupe against the CAPI side).
			if (eventId) {
				window.fbq('track', 'AddToCart', payload, { eventID: eventId });
			} else {
				window.fbq('track', 'AddToCart', payload);
			}
		} catch (err) {
			if (typeof console !== 'undefined' && console.warn) {
				console.warn('[sentiments] fbq AddToCart bridge:', err);
			}
		}

		return res;
	};
}

// Mutate the fetch args in place — if this is an add-item request, attach
// the X-Sentiments-FB-Event-Id header and return the UUID we generated so
// the caller can pass it to fbq as eventID. Returns null for non-matching
// requests (or if anything goes wrong) so callers know to skip dedup.
function injectEventIdHeader(args) {
	try {
		const url = getUrl(args);
		if (!url || !ADD_API_PATTERN.test(url)) return null;

		const eventId = makeUuid();

		// Two shapes: fetch(url, opts) vs fetch(Request). Cover both.
		const init = args[1];
		const first = args[0];

		if (init && typeof init === 'object') {
			const headers = normalizeHeaders(init.headers);
			headers[EVENT_ID_HEADER] = eventId;
			init.headers = headers;
		} else if (first && typeof first === 'object' && typeof first.headers?.set === 'function') {
			// Request object — Headers instance is mutable via .set()
			first.headers.set(EVENT_ID_HEADER, eventId);
		} else {
			// No options object passed — promote to one
			args[1] = { headers: { [EVENT_ID_HEADER]: eventId } };
		}

		return eventId;
	} catch {
		return null;
	}
}

// Headers can arrive as a plain object, an array of [k,v] tuples, or a
// Headers instance. Coerce to a plain object so we can add a key without
// worrying about which shape we got.
function normalizeHeaders(h) {
	if (!h) return {};
	if (typeof h.entries === 'function') {
		const obj = {};
		for (const [k, v] of h.entries()) obj[k] = v;
		return obj;
	}
	if (Array.isArray(h)) {
		const obj = {};
		for (const [k, v] of h) obj[k] = v;
		return obj;
	}
	return { ...h };
}

// RFC 4122 v4 UUID — uses crypto.getRandomValues when available, falls
// back to Math.random for ancient browsers. Either way the output is
// just a string Meta uses as a dedup key, not a security token.
function makeUuid() {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
		const b = new Uint8Array(16);
		crypto.getRandomValues(b);
		b[6] = (b[6] & 0x0f) | 0x40;
		b[8] = (b[8] & 0x3f) | 0x80;
		const h = Array.from(b, (n) => n.toString(16).padStart(2, '0'));
		return `${h.slice(0,4).join('')}-${h.slice(4,6).join('')}-${h.slice(6,8).join('')}-${h.slice(8,10).join('')}-${h.slice(10,16).join('')}`;
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
	});
}

// ── helpers ─────────────────────────────────────────────────────────────

function getUrl(args) {
	const first = args[0];
	if (typeof first === 'string') return first;
	if (first && typeof first.url === 'string') return first.url; // Request object
	return '';
}

// Inspect the request body to find which product/variation was actually
// being added. Handles both shapes:
//   /cart/add-item  body: { id, quantity, variation: [...] }
//   /batch          body: { requests: [{ path: '/.../cart/add-item', data: {...} }, ...] }
function readRequestedItem(args) {
	const body = args[1]?.body;
	if (!body || typeof body !== 'string') return null;
	try {
		const parsed = JSON.parse(body);
		if (parsed.id || parsed.product_id) return parsed;
		if (Array.isArray(parsed.requests)) {
			const addReq = parsed.requests.find(
				(r) => typeof r?.path === 'string' && /\/cart\/add-item/.test(r.path)
			);
			return addReq?.data || null;
		}
	} catch {
		// non-JSON body — not an Interactivity-API add request
	}
	return null;
}

// Build the FB AddToCart custom_data payload from the WC cart response.
//
//   content_ids:  [productId] (string array, FB convention)
//   content_type: 'product' or 'product_group' (variable parent)
//   value:        numeric price in major units
//   currency:     ISO code, default INR
function buildAddToCartPayload(cart, requested) {
	// /batch response wraps the cart inside responses[0].body
	const cartObj = cart?.responses?.[0]?.body || cart;
	const items = Array.isArray(cartObj?.items) ? cartObj.items : [];
	if (!items.length) return null;

	// Best heuristic to find the just-added line item:
	//   1. Match by requested id (the request body told us)
	//   2. Fall back to the last item in the cart (newest item)
	const requestedId = requested?.id || requested?.product_id;
	let item = null;
	if (requestedId) {
		item = items.find((i) => i.id === requestedId || i.parent_product?.id === requestedId);
	}
	if (!item) item = items[items.length - 1];
	if (!item) return null;

	// WC Store API returns prices.price as a string in MINOR units
	// (e.g. "129900" = ₹1299.00 for INR). currency_minor_unit tells us
	// how many decimals to shift.
	const minor = Number(item?.prices?.currency_minor_unit ?? 2);
	const rawPrice = item?.prices?.price ?? '0';
	const value = Number(rawPrice) / Math.pow(10, minor);
	const currency = item?.prices?.currency_code || 'INR';

	// content_ids should be the parent product ID per FB convention; many
	// merchants also pass the variation. For a variable product we pass
	// the parent id and mark content_type as product_group.
	const isVariation = Boolean(item.variation && Object.keys(item.variation).length);
	const productId = isVariation
		? (item?.parent_product?.id || item.id)
		: item.id;

	return {
		content_ids: [String(productId)],
		content_type: isVariation ? 'product_group' : 'product',
		value: Number.isFinite(value) ? Number(value.toFixed(2)) : 0,
		currency,
	};
}
